import { Link } from "react-router-dom";
import { Clock, MapPin, Bus, Users, Accessibility, ExternalLink } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { TICKET_SHOP_URL, CONTACT } from "@/lib/media";
import Reveal, { Kicker } from "@/components/Reveal";
import Seo, { SITE } from "@/components/Seo";
import { Button } from "@/components/ui/button";

const ORG = {
  "@type": "Organization",
  name: "Latschenwirt Gastronomie GmbH",
  url: `${SITE}/`,
  email: "office@latschenwirt.at",
  telephone: "+43 6247 84476",
};

const VENUE = {
  "@type": "EventVenue",
  name: "Latschenwirt-Arena",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Buchhöhstraße 122",
    addressLocality: "Großgmain",
    addressRegion: "Salzburg",
    postalCode: "5084",
    addressCountry: "AT",
  },
};

const OFFERS = [
  { "@type": "Offer", name: "Arena Ticket (Early Bird)", price: "35.00", priceCurrency: "EUR", url: TICKET_SHOP_URL },
  { "@type": "Offer", name: "VIP-Ticket", price: "150.00", priceCurrency: "EUR", url: TICKET_SHOP_URL },
];

const DAYS = {
  "20": {
    date: "2026-11-20",
    path: "/krampus-show-2026-11-20",
    dayDe: "Freitag, 20. November 2026",
    dayEn: "Friday, 20 November 2026",
  },
  "21": {
    date: "2026-11-21",
    path: "/krampus-show-2026-11-21",
    dayDe: "Samstag, 21. November 2026",
    dayEn: "Saturday, 21 November 2026",
  },
};

const MAPS_LAT = "https://www.google.com/maps/search/?api=1&query=Buchh%C3%B6hstra%C3%9Fe+122%2C+5084+Gro%C3%9Fgmain";
const MAPS_MUSEUM = "https://www.google.com/maps/search/?api=1&query=Hasenweg+1%2C+5084+Gro%C3%9Fgmain";

export function FactRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4 border border-white/10 bg-hell-coal p-5">
      <Icon className="w-5 h-5 text-hell-gold shrink-0 mt-0.5" />
      <div>
        <p className="font-cinzel text-[10px] tracking-[0.25em] uppercase text-hell-smoke mb-1">{label}</p>
        <p className="text-hell-bone text-sm leading-relaxed">{value}</p>
      </div>
    </div>
  );
}

export function EventPage({ day }) {
  const { lang } = useLang();
  const d = DAYS[day];
  const de = lang === "de";
  const dayLabel = de ? d.dayDe : d.dayEn;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Event",
        "@id": `${SITE}${d.path}#event`,
        name: `Krampus-Show 2026 – ${dayLabel}`,
        description: de
          ? `IN CULINA DIABOLI – die Krampus-Show des Latschenwirt am ${d.dayDe} in Großgmain bei Salzburg. Einlass 17:30 Uhr, Showbeginn 20:30 Uhr. Über hundert Krampusse, Stunts und Feuerbilder.`
          : `IN CULINA DIABOLI – the Krampus Show at the Latschenwirt on ${d.dayEn} in Grossgmain near Salzburg. Doors open 5:30 pm, show starts 8:30 pm. Over a hundred Krampusse, stunts and fire images.`,
        image: [`${SITE}/og-image.jpg`],
        startDate: `${d.date}T20:30:00+01:00`,
        doorTime: `${d.date}T17:30:00+01:00`,
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        location: VENUE,
        organizer: ORG,
        offers: OFFERS,
        inLanguage: de ? "de" : "en",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: de ? "Start" : "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: `Krampus-Show ${d.dayDe}`, item: `${SITE}${d.path}` },
        ],
      },
    ],
  };

  return (
    <main data-testid={`event-page-${day}`} className="pt-28 md:pt-36 pb-24">
      <Seo
        path={d.path}
        title={`Krampus-Show am ${de ? `${day}. November 2026` : `${day} November 2026`} | Latschenwirt Großgmain`}
        titleEn={`Krampus Show on ${day} November 2026 | Latschenwirt Grossgmain`}
        description={`Krampus-Show am ${d.dayDe}: Einlass 17:30 Uhr, Showbeginn 20:30 Uhr, Latschenwirt-Arena Großgmain bei Salzburg. Parken am Freilichtmuseum mit Gratis-Shuttle. Tickets ab € 35.`}
        descriptionEn={`Krampus Show on ${d.dayEn}: doors open 5:30 pm, show starts 8:30 pm, Latschenwirt Arena in Grossgmain near Salzburg. Parking at the open-air museum with free shuttle. Tickets from € 35.`}
        jsonLd={jsonLd}
      />
      <div className="max-w-4xl mx-auto px-5 md:px-10">
        <Reveal className="mb-12 text-center">
          <Kicker>{de ? "Veranstaltung" : "Event"}</Kicker>
          <h1 className="font-display text-4xl md:text-6xl headline-gold mt-4 ember-glow">
            Krampus-Show 2026 – {dayLabel}
          </h1>
          <p className="text-hell-smoke max-w-2xl mx-auto mt-6 text-base md:text-lg">
            {de
              ? "IN CULINA DIABOLI entführt euch in eine Nacht zwischen Brauchtum und Fantasie – über hundert Krampusse, geheimnisvolle Gestalten, Stunts und eigens erschaffene Feuerbilder in der Latschenwirt-Arena in Großgmain bei Salzburg."
              : "IN CULINA DIABOLI carries you away into a night between folklore and fantasy – over a hundred Krampusse, mysterious figures, stunts and specially created fire images at the Latschenwirt Arena in Grossgmain near Salzburg."}
          </p>
        </Reveal>

        <Reveal className="grid sm:grid-cols-2 gap-4 mb-12">
          <FactRow icon={Clock} label={de ? "Einlass & Rahmenprogramm" : "Doors & supporting programme"} value={de ? "17:30 Uhr – höllisch gutes Rahmenprogramm vor Ort" : "5:30 pm – hellishly good supporting programme on site"} />
          <FactRow icon={Clock} label={de ? "Showbeginn" : "Show starts"} value={de ? "20:30 Uhr" : "8:30 pm"} />
          <FactRow icon={MapPin} label={de ? "Ort" : "Venue"} value={de ? "Latschenwirt-Arena, Buchhöhstraße 122, 5084 Großgmain bei Salzburg" : "Latschenwirt Arena, Buchhöhstraße 122, 5084 Grossgmain near Salzburg"} />
          <FactRow icon={Bus} label={de ? "Shuttlebus (gratis)" : "Shuttle bus (free)"} value={de ? "Ab 17:00 Uhr ab Freilichtmuseum Großgmain, Hasenweg 1" : "From 5:00 pm from the Grossgmain open-air museum, Hasenweg 1"} />
          <FactRow icon={Users} label={de ? "Alter" : "Age"} value={de ? "Eintritt ab 16 Jahren" : "Admission from 16 years"} />
          <FactRow icon={Accessibility} label={de ? "Barrierefreiheit" : "Accessibility"} value={de ? "Die Arena ist teilweise barrierefrei" : "The arena is partially accessible"} />
        </Reveal>

        <Reveal className="border border-hell-gold/30 bg-hell-black p-6 md:p-8 mb-12">
          <h2 className="font-cinzel text-xs tracking-[0.3em] uppercase text-hell-gold mb-3">
            {de ? "Wichtig: Anreise & Parken" : "Important: getting there & parking"}
          </h2>
          <p className="text-hell-bone/75 text-sm leading-relaxed mb-4">
            {de
              ? "Beim Latschenwirt gibt es keine Parkplätze. Bitte parke am Freilichtmuseum Großgmain (Hasenweg 1, 5084 Großgmain) – von dort bringt dich der gratis Shuttlebus ab 17:00 Uhr direkt zur Show."
              : "There is no parking at the Latschenwirt. Please park at the Grossgmain open-air museum (Hasenweg 1, 5084 Grossgmain) – a free shuttle bus takes you directly to the show from 5:00 pm."}
          </p>
          <div className="flex flex-wrap gap-3">
            <a href={MAPS_MUSEUM} target="_blank" rel="noopener noreferrer" className="font-cinzel text-[11px] tracking-[0.15em] uppercase text-hell-gold border border-hell-gold/40 px-5 py-2.5 rounded-full hover:bg-hell-gold hover:text-hell-black transition-colors">
              {de ? "Route zum Parkplatz" : "Directions to parking"} <ExternalLink className="inline w-3 h-3 ml-1" />
            </a>
            <Link to={`${de ? "" : "/en"}/anreise`} className="font-cinzel text-[11px] tracking-[0.15em] uppercase text-hell-bone/70 border border-white/15 px-5 py-2.5 rounded-full hover:text-hell-gold hover:border-hell-gold/40 transition-colors">
              {de ? "Alle Anreise-Infos" : "All travel info"}
            </Link>
          </div>
        </Reveal>

        <Reveal className="text-center">
          <h2 className="font-display text-3xl md:text-4xl text-hell-bone mb-3">
            {de ? "Tickets für diesen Termin" : "Tickets for this date"}
          </h2>
          <p className="text-hell-smoke text-sm mb-6">
            {de ? "Arena-Ticket (Early Bird) € 35,00 · VIP-Ticket € 150,00 – Buchung über den offiziellen Ticketshop." : "Arena ticket (early bird) € 35.00 · VIP ticket € 150.00 – book via the official ticket shop."}
          </p>
          <a href={TICKET_SHOP_URL} target="_blank" rel="noopener noreferrer">
            <Button data-testid={`event-cta-${day}`} className="rounded-full bg-hell-red hover:bg-hell-redhot text-hell-bone font-cinzel tracking-[0.15em] uppercase text-xs px-8 h-12 transition-all duration-300 hover:shadow-[0_0_20px_rgba(139,0,0,0.6)]">
              {de ? "Jetzt Ticket sichern" : "Get your ticket"}
            </Button>
          </a>
          <p className="mt-6">
            <Link to={`${de ? "" : "/en"}/tickets`} className="font-cinzel text-[11px] tracking-[0.2em] uppercase text-hell-gold hover:underline">
              {de ? "Alle Ticketkategorien im Vergleich" : "Compare all ticket categories"}
            </Link>
          </p>
        </Reveal>
      </div>
    </main>
  );
}

export function Anreise() {
  const { lang } = useLang();
  const de = lang === "de";

  const faqs = de
    ? [
        ["Wo findet die Krampus-Show statt?", "In der Latschenwirt-Arena, Buchhöhstraße 122, 5084 Großgmain bei Salzburg."],
        ["Wo kann ich parken?", "Beim Latschenwirt gibt es keine Parkplätze. Bitte parke am Freilichtmuseum Großgmain (Hasenweg 1, 5084 Großgmain). Von dort fährt ab 17:00 Uhr ein gratis Shuttlebus zur Show."],
        ["Wann beginnt die Show?", "Einlass ist um 17:30 Uhr mit Rahmenprogramm vor Ort, Showbeginn ist um 20:30 Uhr."],
        ["Ab welchem Alter ist die Show?", "Der Eintritt ist ab 16 Jahren."],
        ["Ist die Arena barrierefrei?", "Die Arena ist teilweise barrierefrei. Bei Fragen erreichst du uns unter office@latschenwirt.at oder +43 (0)6247 84476."],
      ]
    : [
        ["Where does the Krampus Show take place?", "At the Latschenwirt Arena, Buchhöhstraße 122, 5084 Grossgmain near Salzburg."],
        ["Where can I park?", "There is no parking at the Latschenwirt. Please park at the Grossgmain open-air museum (Hasenweg 1, 5084 Grossgmain). A free shuttle bus runs from there to the show from 5:00 pm."],
        ["When does the show start?", "Doors open at 5:30 pm with a supporting programme on site; the show starts at 8:30 pm."],
        ["From what age is the show?", "Admission is from 16 years."],
        ["Is the arena accessible?", "The arena is partially accessible. If you have questions, contact us at office@latschenwirt.at or +43 (0)6247 84476."],
      ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(([q, a]) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  return (
    <main data-testid="anreise-page" className="pt-28 md:pt-36 pb-24">
      <Seo
        path="/anreise"
        title="Anreise, Parken & Shuttle | Krampus-Show 2026 Latschenwirt"
        titleEn="Getting There, Parking & Shuttle | Krampus Show 2026 Latschenwirt"
        description="Anreise zur Krampus-Show in Großgmain bei Salzburg: Parken am Freilichtmuseum (Hasenweg 1), gratis Shuttlebus ab 17:00 Uhr, Einlass 17:30 Uhr, Showbeginn 20:30 Uhr."
        descriptionEn="Getting to the Krampus Show in Grossgmain near Salzburg: parking at the open-air museum (Hasenweg 1), free shuttle bus from 5:00 pm, doors open 5:30 pm, show starts 8:30 pm."
        jsonLd={faqJsonLd}
      />
      <div className="max-w-4xl mx-auto px-5 md:px-10">
        <Reveal className="mb-12 text-center">
          <Kicker>{de ? "Gut zu wissen" : "Good to know"}</Kicker>
          <h1 className="font-display text-4xl md:text-6xl headline-gold mt-4 ember-glow">
            {de ? "Anreise & Besucherinfos" : "Getting there & visitor info"}
          </h1>
        </Reveal>

        <Reveal className="grid sm:grid-cols-2 gap-4 mb-12">
          <FactRow icon={MapPin} label={de ? "Veranstaltungsort" : "Venue"} value={<>{CONTACT.address} <a href={MAPS_LAT} target="_blank" rel="noopener noreferrer" className="text-hell-gold hover:underline ml-1">{de ? "Route" : "Directions"}</a></>} />
          <FactRow icon={MapPin} label={de ? "Parkplatz & Shuttle" : "Parking & shuttle"} value={<>{de ? "Freilichtmuseum Großgmain, Hasenweg 1 – " : "Grossgmain open-air museum, Hasenweg 1 – "}<a href={MAPS_MUSEUM} target="_blank" rel="noopener noreferrer" className="text-hell-gold hover:underline">{de ? "Route" : "Directions"}</a></>} />
          <FactRow icon={Bus} label={de ? "Gratis Shuttlebus" : "Free shuttle bus"} value={de ? "Ab 17:00 Uhr vom Freilichtmuseum direkt zur Arena und retour" : "From 5:00 pm from the open-air museum to the arena and back"} />
          <FactRow icon={Clock} label={de ? "Ablauf des Abends" : "Schedule"} value={de ? "17:00 Shuttle ab Freilichtmuseum · 17:30 Einlass & Rahmenprogramm · 20:30 Showbeginn" : "5:00 pm shuttle from the museum · 5:30 pm doors & supporting programme · 8:30 pm show starts"} />
        </Reveal>

        <Reveal className="border border-hell-gold/30 bg-hell-black p-6 md:p-8 mb-12">
          <p className="text-hell-bone/75 text-sm leading-relaxed">
            {de
              ? "Bitte beachte: Direkt beim Latschenwirt stehen keine Parkplätze zur Verfügung. Nutze den Parkplatz am Freilichtmuseum Großgmain und den kostenlosen Shuttlebus."
              : "Please note: there is no parking directly at the Latschenwirt. Use the car park at the Grossgmain open-air museum and the free shuttle bus."}
          </p>
        </Reveal>

        <Reveal>
          <h2 className="font-display text-3xl md:text-4xl text-hell-bone mb-8 text-center">
            {de ? "Besucherfragen" : "Visitor questions"}
          </h2>
          <div className="space-y-4">
            {faqs.map(([q, a], i) => (
              <div key={i} data-testid={`faq-${i}`} className="border border-white/10 bg-hell-coal p-6">
                <h3 className="font-cinzel text-sm tracking-[0.1em] uppercase text-hell-gold mb-2">{q}</h3>
                <p className="text-hell-bone/70 text-sm leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10 flex flex-wrap justify-center gap-4">
            <a href={TICKET_SHOP_URL} target="_blank" rel="noopener noreferrer">
              <Button data-testid="anreise-ticket-cta" className="rounded-full bg-hell-red hover:bg-hell-redhot text-hell-bone font-cinzel tracking-[0.15em] uppercase text-xs px-8 h-12">
                {de ? "Jetzt Ticket sichern" : "Get your ticket"}
              </Button>
            </a>
            <Link to={`${de ? "" : "/en"}/krampus-show-2026-11-20`} className="font-cinzel text-[11px] tracking-[0.15em] uppercase text-hell-bone/70 border border-white/15 px-6 py-3 rounded-full hover:text-hell-gold hover:border-hell-gold/40 transition-colors">
              {de ? "Freitag, 20.11." : "Friday, 20 Nov"}
            </Link>
            <Link to={`${de ? "" : "/en"}/krampus-show-2026-11-21`} className="font-cinzel text-[11px] tracking-[0.15em] uppercase text-hell-bone/70 border border-white/15 px-6 py-3 rounded-full hover:text-hell-gold hover:border-hell-gold/40 transition-colors">
              {de ? "Samstag, 21.11." : "Saturday, 21 Nov"}
            </Link>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
